import { ComboboxDemo } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { PageNavigationHeader } from "@/features/license-fee/list/layouts/page-navigation-header";
import { getInputProps, useForm } from "@conform-to/react";
import { useNavigate, Form, useLoaderData } from "react-router-dom";
import { parseWithZod } from "@conform-to/zod";
import { createSchema } from "./private-office-contracts-update.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/inputs/input";
import { useCallback, useEffect, useState, useRef } from "react";
import { LoaderData } from "./loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoneyInput } from "@/components/inputs/money-input/money-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardTitle,
  CardDescription,
  CardHeader,
  CardContent,
} from "@/components/ui/card";
import { usePrivateOfficeAssetsDropzone } from "@/hooks/usePrivateOfficeAssetsDropzone";
import { PoAssetsDropzone } from "@/components/po-assets-dropzone";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio";
import { poTypeOptions } from "@/features/license-fee/list/utils/private-office-contract-mappings";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { BadgeInfo, SirenIcon, Upload } from "lucide-react";
import { useGetPackages } from "@/hooks/use-get-packages/use-get-packages";
import { formatInTimeZone } from "date-fns-tz";
import { CalcCreditsToConsume } from "@/features/private-room/bookings/adapters/get-privateroom-price-by-company-package";
import { apiHubspotIntegrationMs } from "@/services/api-hubspot-integration-ms";
import { parseMoneyValue } from "@/utils/parse-money";
import {
  TAKE_RATE_EXCEPTION_FORM_FIELDS,
  isTakeRateException,
} from "@/features/private-room/bookings/adapters/take-rate-exception";
import { TakeRateExceptionFields } from "@/features/private-room/bookings/layouts/take-rate-exception-fields";
import { ConvertPackageDuration } from "@/utils/convert-package-duration";
import { ConvertProductToName } from "@/utils/convert-product-to-name";
import { CalculateCycleConsumption } from "@/utils/calculate-cycle-consumption";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Toaster } from "@/components/ui/toaster";
import { useUserSelection } from "@/hooks/useUserSelection";
import { useCompanySelection } from "@/hooks/useCompanySelection";
import { usePoContractsUpdateFlow } from "@/features/private-room/bookings/context/use-po-contracts-update-flow/use-po-contracts-update-flow";
import { PoContractUpdateData } from "@/features/private-room/bookings/context/use-po-contracts-update-flow/use-po-contracts-update-flow.types";
import { isProRataDate } from "@/utils/is-pro-rata-date";

const validateHubspotDealId = async (dealId: string): Promise<boolean> => {
  try {
    const response = await apiHubspotIntegrationMs.get(`/deals/${dealId}`);
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

export default function PrivateOfficeContractsUpdate() {
  const navigate = useNavigate();
  const {
    contractTypeOptions,
    renewalIndicatorOptions,
    contractInitialValues,
    selectedCompany,
    selectedSpace,
    selectedPrivateOffice,
    minStartDate,
    minEndDate,
    contractId,
  } = useLoaderData() as LoaderData;

  const {
    handleSubmitPoContractUpdate,
    setSelectedPackageGroups,
    setSelectedPackage,
    setSelectedCycle,
    poContractUpdateData,
    setPoInitialContractData,
    poInitialContractData,
  } = usePoContractsUpdateFlow();

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null,
  );
  const [dealIdError, setDealIdError] = useState<string | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);
  const [isUndeterminedTime, setIsUndeterminedTime] = useState(false);
  const [creditConsumption, setCreditConsumption] = useState<number>(0);
  const [availableCycleCredits, setAvailableCycleCredits] = useState<number>(0);
  const [previousActiveUnitPrice, setPreviousActiveUnitPrice] =
    useState<number>();
  const monthlyCreditsInputRef = useRef<HTMLInputElement>(null);
  // The initial load fetches the active cycle, which moves `activeUnitPrice`
  // exactly like a user interaction would. Without this flag the recalculation
  // below overwrites the stored monthlyCredits before the user touches
  // anything, silently replacing a deliberately negotiated value.
  const isUserDrivenCycleChange = useRef(false);
  const [initialRepassValue, setInitialRepassValue] = useState<number>(0);
  const [selectedContractResponsibleUser, setSelectedContractResponsibleUser] =
    useState<{ name: string; id: number }>();
  const [selectedRoomResponsibleUser, setSelectedRoomResponsibleUser] =
    useState<{ name: string; id: number }>();

  const { toast } = useToast();
  const createToast = () => {
    toast({
      title: "Crédito menor que R$80",
      description: "Atualizamos o valor dos créditos mensais.",
      duration: 4000,
      className: "fixed top-24 right-4 w-[400px] border border-gray-200",
      action: (
        <ToastAction
          altText="Visualizar"
          onClick={() => monthlyCreditsInputRef.current?.focus()}
          className="border border-gray-200 px-3 py-2 rounded-md font-medium"
        >
          Visualizar
        </ToastAction>
      ),
    });
  };

  const { companyUsers, handleSearchUsersByCompanyId } = useUserSelection({});

  const { companyPackages, handleCompanySelect } = useCompanySelection({});

  const [form, fields] = useForm({
    defaultValue: poContractUpdateData
      ? {
          ...poContractUpdateData,
          partnerValue: parseMoneyValue(
            poContractUpdateData.partnerValue,
          ).toString(),
          packageId: poContractUpdateData.packageId.toString(),
          contractResponsibleUserId:
            poContractUpdateData.contractResponsibleUserId?.toString(),
          roomResponsibleUserId:
            poContractUpdateData.roomResponsibleUserId?.toString(),
          dealId: poContractUpdateData.dealId.toString(),
        }
      : contractInitialValues
        ? {
            ...contractInitialValues,
            renewalIndicator: contractInitialValues.renewalIndicator || "",
            notes: contractInitialValues.notes || "",
            monthlyCredits: parseFloat(
              contractInitialValues.monthlyCredits.toString(),
            ),
          }
        : {},
    onValidate({ formData }) {
      const result = parseWithZod(formData, {
        schema: createSchema(
          contractInitialValues.startDate,
          contractInitialValues.endDate || undefined,
        ),
      });
      if (formData.get("dealId") && dealIdError) {
        return {
          ...result,
          status: "error",
          payload: result.payload,
          error: { dealId: [dealIdError] },
          reply: result.reply,
        };
      }
      if (formData.get("startDate") && startDateError) {
        return {
          ...result,
          status: "error",
          payload: result.payload,
          error: { startDate: [startDateError] },
          reply: result.reply,
        };
      }
      return result;
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  const {
    myFiles: acceptanceTermFiles,
    onDrop: onAcceptanceTermDrop,
    removeAt: removeAcceptanceTermFile,
    loading: acceptanceTermLoading,
    replaceFile: replaceAcceptanceTermFile,
  } = usePrivateOfficeAssetsDropzone({
    maxFiles: 1,
    assetType: "partner_assignment_term",
    form,
    fieldName: fields.acceptanceTerm.name,
    defaultValues: poContractUpdateData?.acceptanceTermFiles
      ? [
          {
            id: poContractUpdateData.acceptanceTermFiles!.id.toString(),
            url: poContractUpdateData.acceptanceTermFiles!.url,
            name: poContractUpdateData.acceptanceTermFiles!.name,
          },
        ]
      : contractInitialValues.acceptanceTermFiles
        ? [
            {
              id: contractInitialValues.acceptanceTermFiles!.id.toString(),
              url: contractInitialValues.acceptanceTermFiles!.url,
              name: contractInitialValues.acceptanceTermFiles!.name,
            },
          ]
        : [],
  });

  const {
    myFiles: knowledgeTermFiles,
    onDrop: onKnowledgeTermDrop,
    removeAt: removeKnowledgeTermFile,
    loading: knowledgeTermLoading,
    replaceFile: replaceKnowledgeTermFile,
  } = usePrivateOfficeAssetsDropzone({
    maxFiles: 1,
    assetType: "company_assignment_term",
    form,
    fieldName: fields.knowledgeTerm.name,
    defaultValues: poContractUpdateData?.knowledgeTermFiles
      ? [
          {
            id: poContractUpdateData.knowledgeTermFiles!.id.toString(),
            url: poContractUpdateData.knowledgeTermFiles!.url,
            name: poContractUpdateData.knowledgeTermFiles!.name,
          },
        ]
      : contractInitialValues.knowledgeTermFiles
        ? [
            {
              id: contractInitialValues.knowledgeTermFiles!.id.toString(),
              url: contractInitialValues.knowledgeTermFiles!.url,
              name: contractInitialValues.knowledgeTermFiles!.name,
            },
          ]
        : [],
  });

  const {
    getActiveCycle,
    getPackageDetails,
    packageDetails,
    activeCycle,
    activeUnitPrice,
  } = useGetPackages();

  const handlePackageSelect = useCallback(
    (packageId: string) => {
      if (activeUnitPrice) {
        setPreviousActiveUnitPrice(activeUnitPrice);
      }

      setSelectedPackageId(packageId);
      getPackageDetails(
        Number(packageId),
        Number(contractInitialValues.companyId),
      );
      if (form.value?.startDate) {
        getActiveCycle(
          form.value?.startDate,
          Number(packageId),
          Number(contractInitialValues.companyId),
          false,
        );
      }
    },
    [activeUnitPrice, form.value?.startDate],
  );

  const handleStartDateSelect = useCallback(
    async (startDate: string) => {
      if (contractInitialValues.companyId && selectedPackageId) {
        await getActiveCycle(
          startDate,
          Number(selectedPackageId),
          Number(contractInitialValues.companyId),
          false,
        );
        form.validate({ name: fields.startDate.name });
      }
    },
    [selectedPackageId, contractInitialValues.companyId],
  );

  const calculateRepass = useCallback(
    (takeRate: number) => {
      if (!Number.isFinite(takeRate) || takeRate <= 0) {
        form.update({
          name: fields.repass.name,
          value: 0,
        });
        return;
      }

      const partnerValue = parseMoneyValue(form.value?.partnerValue ?? "0");
      const takeFee = partnerValue * (takeRate / 100);
      const repass = partnerValue - takeFee;
      form.update({
        name: fields.repass.name,
        value: repass.toFixed(2),
      });
      setInitialRepassValue(Number(repass.toFixed(2)));
    },
    [form.value?.partnerValue],
  );

  const handleSubmit = useCallback(() => {
    form.validate();
    if (form.value && form.valid && !dealIdError) {
      if (packageDetails) {
        setSelectedPackage({
          ...packageDetails,
          groups: packageDetails?.groups.map((group) => group.id),
        });
      }

      if (activeCycle) {
        setSelectedCycle(activeCycle);
      }

      handleSubmitPoContractUpdate({
        ...form.value,
        company: contractInitialValues.company,
        space: contractInitialValues.space,
        privateOffice: selectedPrivateOffice,
        acceptanceTermFiles: acceptanceTermFiles[0],
        knowledgeTermFiles: knowledgeTermFiles[0],
        contractResponsibleUser: selectedContractResponsibleUser,
        roomResponsibleUser: selectedRoomResponsibleUser,
        hasUndeterminedTime: isUndeterminedTime,
        id: contractId,
      } as unknown as PoContractUpdateData);

      if (poInitialContractData) {
        setPoInitialContractData({
          ...poInitialContractData,
          repass: initialRepassValue,
        });
      }
    }
  }, [form, contractInitialValues]);

  const calculateMonthlyCredits = useCallback(
    (updateField: boolean) => {
      if (
        selectedPrivateOffice &&
        activeUnitPrice &&
        fields.partnerValue.value
      ) {
        const partnerValueNumber = parseMoneyValue(fields.partnerValue.value);
        const privateRoomMonthlyPrice =
          fields.partnerValue.value &&
          partnerValueNumber !== selectedPrivateOffice.monthlyPrice
            ? partnerValueNumber
            : (selectedPrivateOffice.monthlyPrice ?? 0);

        const calculatedCredits = CalcCreditsToConsume(
          activeUnitPrice,
          privateRoomMonthlyPrice,
          { allowFractional: true },
        );

        if (calculatedCredits !== undefined) {
          form.validate({ name: fields.monthlyCredits.name });
          if (updateField) {
            createToast();
            form.update({
              name: fields.monthlyCredits.name,
              value: calculatedCredits,
            });
          }
        }
      }
    },
    [selectedPrivateOffice, activeUnitPrice, fields.partnerValue.value],
  );

  useEffect(() => {
    if (packageDetails?.groups && selectedCompany?.value) {
      setSelectedPackageGroups(packageDetails.groups);
    }
  }, [packageDetails]);

  useEffect(() => {
    calculateMonthlyCredits(
      isUserDrivenCycleChange.current &&
        (previousActiveUnitPrice! < 80 || activeUnitPrice! < 80),
    );
  }, [activeUnitPrice]);

  useEffect(() => {
    if (activeCycle) {
      const { newCreditConsumption, newCreditAmount } =
        CalculateCycleConsumption(
          activeCycle,
          Number(fields.monthlyCredits.value) || 0,
        );

      setCreditConsumption(newCreditConsumption);
      setAvailableCycleCredits(newCreditAmount - newCreditConsumption);
    }
  }, [activeCycle, fields.monthlyCredits.value]);

  useEffect(() => {
    if (!activeCycle && form.value?.startDate) {
      setStartDateError(
        "Não foi possível encontrar um ciclo ativo para a data selecionada",
      );
    } else {
      setStartDateError(null);
    }
  }, [activeCycle, form.value?.startDate]);

  useEffect(() => {
    if (form.value?.partnerValue && form.value?.takeRatePercentage) {
      calculateRepass(Number(form.value?.takeRatePercentage));
    }
  }, [form.value?.partnerValue, form.value?.takeRatePercentage]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (contractInitialValues) {
        const packageId =
          poContractUpdateData?.packageId?.toString() ??
          contractInitialValues.packageId;

        handleCompanySelect({
          companyId: contractInitialValues.companyId,
          hasFilterAvailability: true,
        });
        handleSearchUsersByCompanyId(contractInitialValues.companyId);
        handlePackageSelect(packageId);

        setIsUndeterminedTime(contractInitialValues.undeterminedTimeValue);
        form.update({
          name: fields.hasUndeterminedTime.name,
          value: contractInitialValues.undeterminedTimeValue,
        });

        if (!poContractUpdateData) {
          setPoInitialContractData({
            ...contractInitialValues,
            dealId: contractInitialValues.dealId || "",
            endDate: contractInitialValues.endDate || undefined,
            takeRatePercentage: Number(
              contractInitialValues.takeRatePercentage,
            ),
            renewalIndicator: contractInitialValues.renewalIndicator || "",
            notes: contractInitialValues.notes || "",
            monthlyCredits: Number(contractInitialValues.monthlyCredits),
            packageId: Number(contractInitialValues.packageId),
            contractResponsibleUserId: Number(
              contractInitialValues.contractResponsibleUserId,
            ),
            roomResponsibleUserId: Number(
              contractInitialValues.roomResponsibleUserId,
            ),
            contractResponsibleUser: {
              name: contractInitialValues.contractResponsibleUser?.name,
              id: contractInitialValues.contractResponsibleUser?.id,
            },
            roomResponsibleUser: {
              name: contractInitialValues.roomResponsibleUser?.name,
              id: contractInitialValues.roomResponsibleUser?.id,
            },
            repass: initialRepassValue,
            acceptanceTerm:
              contractInitialValues.acceptanceTermFiles?.id.toString() || "",
            knowledgeTerm:
              contractInitialValues.knowledgeTermFiles?.id.toString() || "",
            hasUndeterminedTime: contractInitialValues.undeterminedTimeValue,
          });
        }
      }
    };

    loadInitialData();
  }, []);

  return (
    <div className=" gap-6 flex flex-col items-center bg-slate-50">
      <Toaster />
      <div className="self-stretch px-8 pb-8 relative flex justify-center items-start gap-8">
        <Form
          className="max-w-[1000px] flex flex-col gap-8"
          method="POST"
          id={form.id}
          onSubmit={form.onSubmit}
        >
          <div className="w-full max-w-[1000px]">
            <PageNavigationHeader
              onClick={() =>
                navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_LIST)
              }
              title="Editando contrato de venda de escritório"
              subtitle={
                <>
                  Associe um escritório privativo à uma empresa contrante.{" "}
                  <span className="font-medium">
                    Campos com asterísco * são obrigatórios.
                  </span>
                </>
              }
            />
          </div>
          <div className="flex flex-col justify-start items-center gap-8">
            <Card className="w-full gap-6">
              <CardHeader>
                {contractInitialValues.status === "active" && (
                  <div className="mt-2 mb-4 px-2.5 py-0.5 bg-blue-300 rounded-full inline-flex gap-1 max-w-[124px]">
                    <BadgeInfo className="w-4 h-4" />
                    <div className="justify-center text-foreground text-xs font-semibold leading-none py-0.5">
                      Contrato ativo
                    </div>
                  </div>
                )}
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Empresa Contratante
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Identifique a empresa responsável por esta contratação e o
                  pacote a ser utilizado.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-h-[72px]">
                        <Label>Empresa *</Label>
                        <div className="flex flex-col gap-1">
                          <ComboboxDemo
                            key={contractInitialValues.companyId}
                            name={contractInitialValues.companyId}
                            emptyPlaceholder="Empresa não encontrada"
                            searchPlaceholder="Busque uma empresa"
                            selectPlaceholder="Busque por nome ou ID"
                            defaultValue={selectedCompany.value}
                            items={[selectedCompany]}
                            shouldFilter={false}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-h-[72px]">
                        <Label>Deal ID (Hubspot) *</Label>
                        <div className="flex flex-col gap-1">
                          <Input
                            label="Deal ID (Hubspot)"
                            hasRemoveTopMargin={true}
                            showlabel={false}
                            placeholder="Adicione o Deal ID"
                            {...getInputProps(fields.dealId, { type: "text" })}
                            onBlur={async (e) => {
                              const dealId = e.target.value;
                              if (!dealId) {
                                setDealIdError(null);
                                return;
                              }

                              const isValid =
                                await validateHubspotDealId(dealId);
                              if (!isValid) {
                                setDealIdError(
                                  "Deal ID inválido ou não encontrado no Hubspot",
                                );
                                form.validate({ name: fields.dealId.name });
                              } else {
                                setDealIdError(null);
                              }
                            }}
                            error={dealIdError || fields.dealId.errors}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-h-[72px]">
                        <Label>Pacote *</Label>
                        <div className="flex flex-col gap-1">
                          <ComboboxDemo
                            key={fields.packageId.key}
                            name={fields.packageId.name}
                            emptyPlaceholder="Pacote não encontrado"
                            searchPlaceholder="Busque um pacote"
                            selectPlaceholder="Pacote"
                            defaultValue={
                              poContractUpdateData?.packageId?.toString() ??
                              contractInitialValues.packageId
                            }
                            items={companyPackages}
                            onSelect={(item) => {
                              isUserDrivenCycleChange.current = true;
                              handlePackageSelect(item);
                            }}
                            disabled={!contractInitialValues.companyId}
                          />
                          <p className="text-red-500 text-sm">
                            {fields.packageId.errors}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-start gap-4">
                  <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                    <Label>Pessoa responsável pelo contrato (cliente)</Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                      <Select
                        key={fields.contractResponsibleUserId.key}
                        name={fields.contractResponsibleUserId.name}
                        defaultValue={fields.contractResponsibleUserId.value}
                        onValueChange={(value) => {
                          form.update({
                            name: fields.contractResponsibleUserId.name,
                            value,
                          });
                          const selectedUser = companyUsers.find(
                            (user) => user.value === value,
                          );
                          if (selectedUser) {
                            setSelectedContractResponsibleUser({
                              name: selectedUser.label,
                              id: Number(selectedUser.value),
                            });
                          }
                          const selectElement =
                            document.activeElement as HTMLElement;
                          if (selectElement) selectElement.blur();
                        }}
                      >
                        <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                          <SelectValue placeholder="Selecione uma pessoa responsável pelo contrato" />
                        </SelectTrigger>
                        <SelectContent>
                          {companyUsers.length === 0 ? (
                            <SelectItem value="no-users" disabled>
                              Empresa não selecionada ou usuários não
                              encontrados
                            </SelectItem>
                          ) : (
                            companyUsers.map((user) => (
                              <SelectItem key={user.value} value={user.value}>
                                {`${user.value} - ${user.label}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-red-500 text-sm">
                        {fields.contractResponsibleUserId.errors}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                    <Label>
                      Pessoa responsável por gestão das salas (cliente)
                    </Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                      <Select
                        key={fields.roomResponsibleUserId.key}
                        name={fields.roomResponsibleUserId.name}
                        defaultValue={fields.roomResponsibleUserId.value}
                        onValueChange={(value) => {
                          form.update({
                            name: fields.roomResponsibleUserId.name,
                            value,
                          });
                          const selectedUser = companyUsers.find(
                            (user) => user.value === value,
                          );
                          if (selectedUser) {
                            setSelectedRoomResponsibleUser({
                              name: selectedUser.label,
                              id: Number(selectedUser.value),
                            });
                          }
                          const selectElement =
                            document.activeElement as HTMLElement;
                          if (selectElement) selectElement.blur();
                        }}
                      >
                        <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                          <SelectValue placeholder="Selecione uma pessoa responsável pelas salas" />
                        </SelectTrigger>
                        <SelectContent>
                          {companyUsers.length === 0 ? (
                            <SelectItem value="no-users" disabled>
                              Empresa não selecionada ou usuários não
                              encontrados
                            </SelectItem>
                          ) : (
                            companyUsers.map((user) => (
                              <SelectItem key={user.value} value={user.value}>
                                {`${user.value} - ${user.label}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-red-500 text-sm">
                        {fields.roomResponsibleUserId.errors}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Espaço Contratado
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Selecione o espaço parceiro e o escritório privativo que será
                  reservado.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Espaço *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <ComboboxDemo
                          key={contractInitialValues.coworkingId}
                          name={contractInitialValues.coworkingId}
                          emptyPlaceholder="Espaço não encontrado"
                          searchPlaceholder="Busque um espaço"
                          selectPlaceholder="Espaço"
                          defaultValue={selectedSpace.value}
                          items={[selectedSpace]}
                          shouldFilter={false}
                          disabled={true}
                        />
                      </div>
                    </div>
                    <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Escritório Privativo *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <ComboboxDemo
                          key={contractInitialValues.privateOfficeId}
                          name={contractInitialValues.privateOfficeId}
                          emptyPlaceholder="Escritório não encontrado"
                          searchPlaceholder="Busque um escritório"
                          selectPlaceholder="Escritório"
                          defaultValue={selectedPrivateOffice?.value.toString()}
                          items={
                            selectedPrivateOffice ? [selectedPrivateOffice] : []
                          }
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Dados da Reserva
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Preencha os detalhes contratuais referentes ao período,
                  consumo e valores negociados do contrato.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-8">
                  <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch inline-flex justify-start items-start gap-4">
                      <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                        <Label>Tipo do contrato *</Label>
                        <div className="self-stretch flex flex-col justify-start items-start gap-1">
                          <Select
                            key={contractInitialValues.contractType}
                            name={contractInitialValues.contractType}
                            defaultValue={contractInitialValues.contractType}
                            disabled={true}
                          >
                            <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                              <SelectValue placeholder="Selecione um tipo de contrato" />
                            </SelectTrigger>
                            <SelectContent>
                              {contractTypeOptions.map((contractType) => (
                                <SelectItem
                                  key={contractType.value}
                                  value={contractType.value}
                                >
                                  {contractType.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-1">
                        <Label>Número de pessoas *</Label>
                        <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                          <div className="w-full">
                            <Input
                              label="Número de pessoas"
                              showlabel={false}
                              placeholder="Digite o número de pessoas"
                              {...getInputProps(fields.members, {
                                type: "number",
                              })}
                              onWheel={(e) => e.currentTarget.blur()}
                            />
                          </div>
                          <p className="text-red-500 text-sm">
                            {fields.members.errors}
                          </p>
                        </div>
                      </div>
                    </div>
                    {contractInitialValues.contractType === "po" && (
                      <div className="flex flex-col gap-2">
                        <Label className="text-sm font-medium">
                          Tipo de escritório
                        </Label>
                        <RadioGroup
                          key={fields.poType.key}
                          name={fields.poType.name}
                          value={
                            (fields.poType.value as string | undefined) ??
                            contractInitialValues.poType ??
                            ""
                          }
                          onValueChange={(value) => {
                            form.update({ name: fields.poType.name, value });
                          }}
                          className="flex gap-4"
                        >
                          {poTypeOptions.map((option) => (
                            <div
                              key={option.value}
                              className="flex items-center space-x-2"
                            >
                              <RadioGroupItem
                                value={option.value}
                                id={option.id}
                                className="border-black text-black"
                              />
                              <Label
                                htmlFor={option.id}
                                className="text-sm font-medium leading-none cursor-pointer"
                              >
                                {option.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    )}
                  </div>
                  <div className="self-stretch flex flex-col justify-start items-start">
                    <div className="self-stretch h-px border border-muted" />
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Label>Data de início *</Label>
                        <div className="self-stretch rounded-md flex flex-col justify-start items-start ">
                          <div className="w-full">
                            <Input
                              label="Data de início"
                              showlabel={false}
                              {...getInputProps(fields.startDate, {
                                type: "date",
                              })}
                              disable={
                                !selectedPrivateOffice &&
                                !contractInitialValues?.spot?.id &&
                                !selectedPackageId
                              }
                              min={minStartDate}
                              onBlur={async (e) => {
                                isUserDrivenCycleChange.current = true;
                                handleStartDateSelect(e.target.value);
                              }}
                            />
                            <p className="text-red-500 text-sm">
                              {startDateError || fields.startDate.errors}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="inline-flex justify-start items-center gap-2">
                        <input
                          type="checkbox"
                          id="hasUndeterminedTime"
                          name={fields.hasUndeterminedTime.name}
                          checked={isUndeterminedTime}
                          onChange={(e) => {
                            setIsUndeterminedTime(e.target.checked);
                            form.update({
                              name: fields.hasUndeterminedTime.name,
                              value: e.target.checked,
                            });
                            if (e.target.checked) {
                              form.update({
                                name: fields.endDate.name,
                                value: undefined,
                              });
                              if (endDateInputRef.current) {
                                endDateInputRef.current.value = "";
                              }
                            }
                          }}
                          className="h-4 w-4 shrink-0 accent-black"
                        />
                        <Label
                          htmlFor="hasUndeterminedTime"
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          Tempo indeterminado
                        </Label>
                      </div>
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Data de término *</Label>
                      <div className="self-stretch rounded-md flex flex-col justify-start items-start">
                        <div className="w-full">
                          <Input
                            ref={endDateInputRef}
                            label="Data de término"
                            showlabel={false}
                            min={minEndDate}
                            {...getInputProps(fields.endDate, { type: "date" })}
                            disable={
                              Boolean(fields.hasUndeterminedTime.value) ||
                              !fields.startDate.value
                            }
                            onBlur={() =>
                              form.validate({ name: fields.endDate.name })
                            }
                            error={fields.endDate.errors}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {isProRataDate(form.value?.startDate ?? "") && (
                    <div className="flex-col justify-start items-start gap-1 self-stretch flex">
                      <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-full flex-col justify-start items-start gap-1 inline-flex">
                        <div className="flex gap-3  items-center pl-6">
                          <div className="absolute top-4 left-4">
                            <SirenIcon
                              className="text-yellow-900"
                              width={16}
                              height={16}
                            />
                          </div>
                          <div>
                            <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                              Atenção: Pró-rata será aplicada!
                            </AlertTitle>
                            <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                              A data de início selecionada não coincide com o
                              início do mês. O sistema calculará automaticamente
                              a quantidade de créditos em pró-rata. Este valor
                              será aplicado ao primeiro ciclo desta reserva.
                            </AlertDescription>
                          </div>
                        </div>
                      </Alert>
                    </div>
                  )}
                  <div className="self-stretch flex flex-col justify-start items-start">
                    <div className="self-stretch h-px border border-muted" />
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Valor do parceiro R$*</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <MoneyInput
                            label="Valor do parceiro"
                            placeholder="Adicione o valor do parceiro"
                            {...getInputProps(fields.partnerValue, {
                              type: "text",
                            })}
                            error={fields.partnerValue.errors}
                            onBlur={() => calculateMonthlyCredits(false)}
                            prefix="R$"
                          />
                        </div>
                      </div>
                      {!fields.partnerValue.errors && (
                        <div className="self-stretch justify-start text-zinc-500 text-sm font-normal">
                          Valor negociado com o parceiro, deve ser o mesmo que
                          consta no termo de cessão.
                        </div>
                      )}
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Créditos mensais *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            ref={monthlyCreditsInputRef}
                            label="Créditos mensais"
                            showlabel={false}
                            placeholder="Digite o número de créditos mensais"
                            {...getInputProps(fields.monthlyCredits, {
                              type: "number",
                              step: "0.01",
                              onBlur: () =>
                                form.validate({
                                  name: fields.monthlyCredits.name,
                                }),
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            disable={
                              !fields.startDate.value ||
                              !fields.partnerValue.value
                            }
                          />
                        </div>
                      </div>
                      {fields.monthlyCredits.errors && (
                        <p className="text-red-500 text-sm self-stretch justify-start">
                          {fields.monthlyCredits.errors}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-start gap-4">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <Label>Porcentagem de Take *</Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                      <div className="w-full">
                        <Input
                          label="Porcentagem de Take"
                          showlabel={false}
                          {...getInputProps(fields.takeRatePercentage, {
                            type: "number",
                          })}
                          onWheel={(e) => e.currentTarget.blur()}
                          error={fields.takeRatePercentage.errors}
                          onChange={(e) => {
                            form.value?.partnerValue &&
                              calculateRepass(Number(e.target.value));
                          }}
                          prefix="%"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <Label>Repasse</Label>
                    <div className="w-full">
                      <Input
                        label="Repasse"
                        showlabel={false}
                        placeholder="-"
                        {...getInputProps(fields.repass, {
                          type: "number",
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        error={fields.repass.errors}
                        disable={
                          !fields.takeRatePercentage.value ||
                          !fields.partnerValue.value
                        }
                        readOnly={true}
                        className="bg-gray-100"
                        prefix="R$"
                      />
                    </div>
                    <div className="self-stretch justify-start text-zinc-500 text-sm font-normal">
                      Valor calculado automaticamente
                    </div>
                  </div>
                </div>
                {isTakeRateException(fields.takeRatePercentage.value) && (
                  <TakeRateExceptionFields
                    justification={
                      fields[TAKE_RATE_EXCEPTION_FORM_FIELDS.justification]
                    }
                    approvedBy={
                      fields[TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedBy]
                    }
                    approvedAt={
                      fields[TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedAt]
                    }
                  />
                )}
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Avisos e Ajustes
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Configure regras específicas para aviso prévio e vigência.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Período de Aviso Prévio do Cliente *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Período de Aviso Prévio do Cliente"
                            showlabel={false}
                            defaultValue={
                              contractInitialValues.noticeInDaysClient
                            }
                            {...getInputProps(fields.noticeInDaysClient, {
                              type: "number",
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={fields.noticeInDaysClient.errors}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Período de Aviso Prévio do Parceiro *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Período de Aviso Prévio do Parceiro"
                            showlabel={false}
                            defaultValue={
                              contractInitialValues.noticeInDaysPartner
                            }
                            {...getInputProps(fields.noticeInDaysPartner, {
                              type: "number",
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={fields.noticeInDaysPartner.errors}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Vigência prevista (em meses) *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Vigência prevista (em meses)"
                            showlabel={false}
                            placeholder="Adicione a vigência prevista (em meses)"
                            {...getInputProps(fields.expectedDurationMonths, {
                              type: "number",
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={fields.expectedDurationMonths.errors}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Índice de reajuste *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Select
                          key={fields.renewalIndicator.key}
                          name={fields.renewalIndicator.name}
                          defaultValue={fields.renewalIndicator.value as string}
                          onValueChange={(value) => {
                            form.update({
                              name: fields.renewalIndicator.name,
                              value,
                            });
                            const selectElement =
                              document.activeElement as HTMLElement;
                            if (selectElement) selectElement.blur();
                          }}
                        >
                          <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                            <SelectValue placeholder="Selecione um índice de reajuste" />
                          </SelectTrigger>
                          <SelectContent>
                            {renewalIndicatorOptions.map((renewalIndicator) => (
                              <SelectItem
                                key={renewalIndicator.value}
                                value={renewalIndicator.value}
                              >
                                {renewalIndicator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-red-500 text-sm">
                          {fields.renewalIndicator.errors}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Documentos
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Anexe os termos formais assinados entre a empresa contratante
                  e o parceiro.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="inline-flex justify-start items-start gap-4">
                  <Card className="bg-slate-50 w-full">
                    <CardHeader className="justify-center items-center">
                      <CardTitle className="text-xl">
                        Termo de Aceite *
                      </CardTitle>
                      <CardDescription className="min-h-[2.5rem]">
                        {`Certifique-se que o termo refere-se a empresa ${contractInitialValues.company.name || ""}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PoAssetsDropzone
                        name={fields.acceptanceTerm.name}
                        onDrop={onAcceptanceTermDrop}
                        files={acceptanceTermFiles}
                        errors={fields.acceptanceTerm.errors}
                        className="bg-white"
                        isLoading={acceptanceTermLoading}
                        removeFile={removeAcceptanceTermFile}
                      />
                      <div className="flex justify-center mt-5">
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          id="acceptance-term-file-input"
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              replaceAcceptanceTermFile(files[0]);
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={(e) => {
                            e.preventDefault();
                            document
                              .getElementById("acceptance-term-file-input")
                              ?.click();
                          }}
                        >
                          <Upload className="w-4 h-4" />
                          Substituir
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-50 w-full">
                    <CardHeader className="justify-center items-center">
                      <CardTitle className="text-xl">
                        Termo de Ciência *
                      </CardTitle>
                      <CardDescription className="min-h-[2.5rem]">
                        {`Certifique-se que o termo refere-se ao espaço ${contractInitialValues.space.name || ""}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PoAssetsDropzone
                        name={fields.knowledgeTerm.name}
                        onDrop={onKnowledgeTermDrop}
                        files={knowledgeTermFiles}
                        errors={fields.knowledgeTerm.errors}
                        className="bg-white"
                        isLoading={knowledgeTermLoading}
                        removeFile={removeKnowledgeTermFile}
                      />
                      <div className="flex justify-center mt-5">
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          id="knowledge-term-file-input"
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              replaceKnowledgeTermFile(files[0]);
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={(e) => {
                            e.preventDefault();
                            document
                              .getElementById("knowledge-term-file-input")
                              ?.click();
                          }}
                        >
                          <Upload className="w-4 h-4" />
                          Substituir
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Observações
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Registre observações relevantes para a operação.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch inline-flex justify-start items-start gap-8">
                  <div className="flex-1 flex justify-start items-start gap-4">
                    <div className="flex-1 h-32 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Notas</Label>
                      <Textarea
                        placeholder="Digite detalhes importantes acerca desta reserva."
                        className="h-32 bg-white border-[#d4d4d8] ring-inset"
                        {...getInputProps(fields.notes, { type: "text" })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Escolha de que forma será feita esta cobrança.
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Você pode atualizar este contrato sem alterar os ciclos do
                  pacote, ou optar por ajustá-los para realizar um upsell.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <RadioGroup
                  name={fields.flowType.name}
                  value={fields.flowType.value}
                  onValueChange={(value) => {
                    form.update({ name: fields.flowType.name, value });
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="flow_type_default" value="default" />
                    <Label
                      htmlFor="flow_type_default"
                      className="cursor-pointer"
                    >
                      Atualizar contrato sem editar ciclos.
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      id="flow_type_upsell"
                      value="upsell"
                      disabled={packageDetails?.duration !== "monthly"}
                    />
                    <Label
                      htmlFor="flow_type_upsell"
                      className={`cursor-pointer ${packageDetails?.duration !== "monthly" ? "text-gray-400 cursor-not-allowed" : ""}`}
                    >
                      Editar ciclos e ajustar créditos.
                    </Label>
                  </div>
                </RadioGroup>

                {fields.flowType.value === "default" &&
                  activeCycle?.credits?.amount &&
                  Number(fields.monthlyCredits?.value) &&
                  activeCycle.credits.amount < creditConsumption &&
                  packageDetails?.duration === "monthly" && (
                    <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-fullgap-1 inline-flex mt-4">
                      <div className="flex gap-3  items-center pl-6">
                        <div className="absolute top-4 left-4">
                          <SirenIcon
                            className="text-yellow-900"
                            width={16}
                            height={16}
                          />
                        </div>
                        <div>
                          <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                            Atenção
                          </AlertTitle>
                          <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                            Os créditos mensais do contrato ultrapassam os
                            créditos disponíveis nos ciclos. Recomendamos editar
                            os ciclos e ajustar os créditos para evitar que os
                            ciclos excedam ao criar este contrato. Se isso for
                            uma exceção permitida, você pode prosseguir com a
                            criação do contrato normalmente.
                          </AlertDescription>
                        </div>
                      </div>
                    </Alert>
                  )}
              </CardContent>
            </Card>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-8">
            <div className="self-stretch inline-flex justify-end items-start gap-2">
              <Button
                variant="outline"
                className="px-4 py-2"
                onClick={() =>
                  navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_LIST)
                }
              >
                Voltar
              </Button>
              <Button
                type="submit"
                className="px-4 py-2"
                disabled={
                  !form.valid ||
                  !form.value?.flowType ||
                  !!dealIdError ||
                  !activeCycle
                }
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                {form.value?.flowType === "upsell"
                  ? "Avançar para pagamentos"
                  : "Avançar para revisão"}
              </Button>
            </div>
          </div>
        </Form>

        <Card className="bg-slate-50 mt-[110px] inline-flex flex-col justify-start items-start gap-2 min-w-[400px] sticky top-16">
          {!packageDetails && !activeCycle && (
            <CardHeader>
              <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                Pacote e Ciclo
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Escolha um pacote e uma data de início para ver mais
                informações.
              </CardDescription>
            </CardHeader>
          )}
          {packageDetails && (
            <>
              <CardHeader className="pb-0 pt-2 px-3">
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Pacote
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 w-full">
                <div className="w-full border rounded-md overflow-hidden">
                  <div className="flex w-full">
                    <div className="flex-1 p-3 border-r border-border">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold">
                          {packageDetails?.id}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          ID do pacote
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-3 border-r border-border">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold">
                          {packageDetails?.startDate}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Data de início
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-3">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold">
                          {packageDetails?.endDate}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Data de fim
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full border-t border-border">
                    <div className="w-full p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {packageDetails?.products
                          ?.map((product) => ConvertProductToName(product))
                          .join(", ")}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Produtos
                      </div>
                    </div>
                  </div>

                  <div className="w-full border-t border-border">
                    <div className="w-full p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {packageDetails?.groups
                          ?.map((group) => group.name)
                          .join(", ") || "Company wide"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Grupos
                      </div>
                    </div>
                  </div>

                  <div className="w-full border-t border-border">
                    <div className="w-full p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {packageDetails?.duration
                          ? ConvertPackageDuration(packageDetails.duration) ||
                            "Mensal"
                          : "Mensal"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Duração do ciclo
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}
          {activeCycle && (
            <>
              <CardHeader className="pb-0 pt-2 px-3">
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Ciclo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 w-full">
                <div className="w-full border rounded-md overflow-hidden">
                  <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {activeCycle?.id}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        ID do ciclo
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {formatInTimeZone(
                          activeCycle?.startDate,
                          "UTC",
                          "dd/MM/yyyy",
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Data de início
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {formatInTimeZone(
                          activeCycle?.endDate,
                          "UTC",
                          "dd/MM/yyyy",
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Data de fim
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {activeCycle?.credits.amount} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Quantidade de créditos
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {Math.max(0, availableCycleCredits)} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Créditos disponíveis
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {creditConsumption} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Utilizados por PO
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        R$ {activeCycle?.credits.unitPrice.toFixed(2)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Valor unitário do crédito
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        R$ {activeCycle?.credits.exceededPrice.toFixed(2)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Valor do excedente
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {activeCycle?.credits.courtesy} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Cortesias
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
